import { ArrowLeftIcon, ListPlusIcon, PlusIcon, SaveIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { ChosenCustomer, CustomerPicker, ItemPicker } from "@/features/invoices/DocumentPickers"
import { DocumentDiscountField } from "@/features/invoices/DiscountField"
import { LineCard, TotalsBar } from "@/features/invoices/InvoiceEditorScreen"
import { DraftConflictAlert } from "@/features/drafts/DraftConflictAlert"
import { DraftNameBadge } from "@/features/drafts/DraftNameBadge"
import { DraftReadyControl } from "@/features/drafts/DraftReadyControl"
import { useDraft, useDraftAutosave } from "@/features/drafts/use-document-draft"
import {
  adoptedLines,
  emptyLine,
  linesFromInvoice,
  makeKeyFactory,
  useCustomerById,
  type EditorLine,
} from "@/features/invoices/use-invoice-editor"
import { useQuotation } from "@/features/quotations/use-quotation"
import {
  emptyQuotationState,
  stateFromQuotation,
  toQuotationApiBody,
  useQuotationPreview,
  useSaveQuotation,
  type QuotationEditorState,
  type TaxDisplayMode,
} from "@/features/quotations/use-quotation-editor"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DateField } from "@/components/DateField"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { requestBlob } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { todayInputValue } from "@/lib/dates"
import { endpoints, type Customer } from "@/lib/endpoints"
import { basisPointsToInputValue } from "@/lib/units"
import { routes } from "@/routes/paths"

/**
 * Writing a quotation (§7, §9.3, §11.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE SCREEN FOR "NEW" AND "REVISE", because the fields, the lines, the preview
 * and §9.1's group rules are identical — and two editors is how one of them ends
 * up missing a field. `:id` in the route is what tells them apart.
 *
 * A SAVED DRAFT AND A `DRAFT` QUOTATION ARE TWO DIFFERENT THINGS (§7.2
 * amendment, 2026-08-15). This comment used to argue that this editor needed no
 * draft mechanism because DRAFT is already a real quotation status. The owner
 * overruled that, and he was right — the two are not substitutes:
 *
 *   A SAVED DRAFT IS A FORM. No number, no row in the register, nothing filed.
 *   It is the half-typed screen the workshop phone call interrupted, and
 *   abandoning it is a DELETE that costs nothing. It lives in
 *   `document_drafts` with `kind = QUOTATION`, alongside the bills' drafts.
 *
 *   A `DRAFT` QUOTATION IS A DOCUMENT. Saving it spends a number out of the
 *   continuous `NNN/YY` series (§6) and puts it in the register where the owner
 *   counts his outstanding offers. Abandoning it means VOIDING it — a voided
 *   quotation the customer never saw, sitting in the book for ever.
 *
 * So: the draft is for while the offer is still being worked out, and saving the
 * quotation is for when it is an offer. READY belongs to the FORM — it says
 * "I have checked this, somebody can issue it" — and it is deliberately NOT a
 * quotation status: those stay DRAFT/SENT/APPROVED/REJECTED/…/VOID (§4, §7),
 * and issuing remains the irreversible act that spends the number.
 *
 * EVERY FIGURE COMES FROM THE SERVER, via `POST /quotations/preview` (§6,
 * CLAUDE.md). Nothing here multiplies a rate by a quantity.
 *
 * THE HEADLINE FIGURE FOLLOWS §9.3. Under TAX_EXTRA the sheet quotes the PRE-TAX
 * subtotal, so the bar at the bottom emphasises that and demotes the
 * tax-inclusive total — the number on the screen and the number on the sheet are
 * the same number, and the mode switch changes both together.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/* See `makeKeyFactory` — `adopt` on the resume path is what stops a restored
 * draft's keys colliding with newly added lines. */
const lineKeys = makeKeyFactory("Q")
const nextKey = lineKeys.next

/**
 * §9.3's two answers, keyed by the enum so a third mode cannot be added to
 * `TaxDisplayMode` and forgotten here. THE VALUES ARE THE STORED ONES — this
 * choice changes what the printed sheet says, so `TAX_EXTRA` and `TAX_COMPUTED`
 * are wire values, not display strings, and must not be "tidied" into labels.
 */
const TAX_MODE_LABEL_KEY: Record<TaxDisplayMode, string> = {
  TAX_EXTRA: "quotation.taxModeExtra",
  TAX_COMPUTED: "quotation.taxModeComputed",
}
const TAX_MODES = Object.keys(TAX_MODE_LABEL_KEY) as TaxDisplayMode[]

/*
 * ═════════════════════════════════════════════════════════════════════════════
 * 44px ROWS, AND THIS IS NOT DECORATION.
 *
 * This field was a native `<select>` on purpose: on a phone the operating system
 * opens its own full-height wheel — bigger than anything drawn in the page,
 * already familiar, and reliably hittable without a 48px target (§11.1). The
 * owner asked for the in-page Select instead, for visual consistency with the
 * rest of the app, and that is his call. But the property the native control was
 * protecting is the one thing the swap must not lose, and shadcn's `SelectItem`
 * is about 28px (`py-1` around `text-sm`) — 16px short of a thumb with dust on
 * it. So the height is put back here, explicitly.
 *
 * ``, NEVER `min-h-[44px]`: `styles/preferences.css` scales the root font
 * size for the large-text preference precisely so touch targets grow with it, and
 * a pixel literal would stay stuck at 44. Unconditional rather than behind
 * `pointer-coarse:` — the workshop phone is the primary device, a desktop mouse
 * loses nothing from a 44px row, and an unconditional class cannot be defeated by
 * a device that misreports its pointer type.
 *
 * The labels are allowed to wrap, because "Work the tax out on the quotation" is
 * wider than a narrow phone's popup and the item's text is `whitespace-nowrap` by
 * default. Do not "tidy" any of this away.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const TOUCH_ITEM = "whitespace-normal [&>div]:whitespace-normal"
/*
 * THE TRIGGER IS DELIBERATELY NOT FORCED TALLER, and it was until
 * 2026-08-16. At 44px it stood a head above the search box and the "More
 * filters" button beside it — both `h-8` — and read as a rendering fault. The
 * OS wheel this replaced was a LIST, so the 44px minimum belongs on the ITEMS
 * (see `TOUCH_ITEM`, which the shared component now applies anyway); the
 * trigger is one control among several the same size. If a toolbar's targets
 * are ever raised, raise every control in it together.
 */

export function QuotationEditorScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const editing = id !== undefined && id !== ""

  const [customer, setCustomer] = useState<Customer | null>(null)
  const [pickingCustomer, setPickingCustomer] = useState(false)
  const [pickingItemFor, setPickingItemFor] = useState<string | null>(null)

  const [state, setState] = useState<QuotationEditorState>(() =>
    emptyQuotationState(todayInputValue(), [emptyLine(nextKey())]),
  )

  // `id` WHEN REVISING, so the server previews the buyer the `PUT` will actually
  // keep (§4) rather than the live customer row — see `editingQuery`.
  const preview = useQuotationPreview(state, editing ? id : undefined)
  const save = useSaveQuotation(id)

  /*
   * TWO WAYS TO ARRIVE WITH CONTENT, and they load through one path:
   *   /quotations/:id/edit   — revise this offer, keeping its number (§7)
   *   /quotations/new?copyFrom=12 — a NEW offer from an old one's lines (§11.3)
   *
   * `stateFromQuotation` is what differs between them: an edit keeps the
   * document's own date and validity, a copy takes today's and drops the
   * customer's P.O. reference, which belonged to the old order.
   *
   * A THIRD WAY — `?draft=7`, below — OUTRANKS THE COPY. A URL carrying both is
   * a link that was built wrongly, and the two seeds would race to fill the same
   * form. The draft wins because it is the owner's own typing; an old
   * quotation's lines are recoverable by pressing "Make a copy" again.
   */
  const [searchParams] = useSearchParams()
  // TRUTHY, NOT `!== null`. A truncated link (`/quotations/new?draft=`) yields
  // "" — not null — so the old check treated it as a real draft id, requested
  // `/quotations/drafts/`, and left the screen inert on the 404.
  const resuming = !editing && (searchParams.get("draft") ?? "") !== ""
  const copyFrom = editing || resuming ? undefined : (searchParams.get("copyFrom") ?? undefined)
  const source = useQuotation(editing ? id : copyFrom)
  const [loaded, setLoaded] = useState(false)

  /*
   * ?draft=7 — resume an unfinished offer (§7.2). The saved form replaces the
   * editor's state ONCE; from then on the autosave below keeps the server copy
   * current.
   *
   * NEW OFFERS ONLY. Revising an existing quotation (`/quotations/:id/edit`)
   * has no draft: that document exists, holds a number, and the `PUT` is where
   * its changes go. A form autosaving beside it would be a second, numberless
   * copy of a document already in the register, with no rule for which wins.
   */
  const draftParam = resuming ? (searchParams.get("draft") ?? undefined) : undefined
  const resumed = useDraft<QuotationEditorState>("QUOTATION", draftParam)
  const [restored, setRestored] = useState(false)
  // A resume that FAILED (already made into a quotation, or discarded on the
  // other device) must not leave autosave switched off for ever behind a
  // blank form — see the `enabled` note below.
  const resumeFailed = draftParam !== undefined && resumed.isError

  useEffect(() => {
    if (restored || resumed.data === undefined) return
    setRestored(true)
    // Hoisted so the payload stays narrowed inside the updater below.
    const saved = resumed.data.payload
    // BEFORE the payload lands in state — see `makeKeyFactory`. The restored
    // lines carry keys from a previous page load, when the counter was higher
    // than it is now; without this the next "Add line" reuses one of them.
    lineKeys.adopt(saved.lines)
    /*
     * MERGED OVER THE EMPTY FORM, NOT ASSIGNED OVER IT — a draft is JSON written
     * by WHATEVER VERSION OF THIS SCREEN saved it, and `document_drafts` has no
     * migration for its payload.
     *
     * `itemNo` (2026-08-15) is the first field to prove it: a draft saved the day
     * before has no such key, a plain assignment left `state.itemNo` undefined,
     * and `text(undefined)` threw on the very next render — a resumed draft that
     * opened to a blank crashed screen with the owner's typing still in the
     * database. Every field added after this one would have done the same.
     *
     * The spread fills only what the payload does not carry, so a resumed draft
     * still wins on every key it does have.
     */
    /*
     * AND `adoptedLines` DOES THE SAME PER LINE, which the spread cannot: it
     * replaces `lines` wholesale, so a field added to `EditorLine` afterwards
     * arrives `undefined` on an older draft. This note used to say "no such
     * field has been added yet"; §15(3)(a)'s `discount` was the first, and it
     * threw inside `LineCard` on the very first render of a resumed draft.
     */
    setState((current) => ({ ...current, ...saved, lines: adoptedLines(saved.lines) }))
    if (saved.customerId !== null && resumed.data.customerName !== "") {
      setCustomer({
        id: saved.customerId,
        name: resumed.data.customerName,
      } as Customer)
    }
  }, [restored, resumed.data])

  const autosave = useDraftAutosave({
    // The mount, and with it the capability the API checks — `quotation.create`
    // here, `invoice.create` on the bills mount. Never a flag on one endpoint.
    kind: "QUOTATION",
    state,
    customerName: customer?.name ?? "",
    draftId: draftParam === undefined ? null : Number(draftParam),
    initialVersion: resumed.data?.version ?? 1,
    initialStatus: resumed.data?.status ?? "DRAFT",
    // Has the resumed GET actually landed? Without this the hook cannot tell
    // "version 1, a new draft" from "version 1, the real one is still in
    // flight", and a resumed draft 409s against itself on its first autosave.
    loaded: resumed.data !== undefined,
    /*
     * NOT WHILE THE RESUMED PAYLOAD IS STILL ON ITS WAY. `state` starts as an
     * empty quotation, and autosaving that over a real draft before its GET
     * lands is the one way this feature could destroy typed work. Off entirely
     * while revising a saved quotation — see the note on `draftParam`.
     */
    /*
     * NOT WHILE A RESUMED PAYLOAD IS STILL IN FLIGHT — saving the EMPTY form
     * over a real draft is the one way autosave could destroy work.
     *
     * `resumeFailed` REOPENS IT. Without that clause a resume that 404s (the
     * draft was already turned into a quotation, or discarded from the shop
     * laptop) left `restored` false for ever, so autosave never switched on and
     * every keystroke after it vanished silently. There is nothing to overwrite
     * in that case — the draft does not exist — so a fresh one is the right
     * outcome, and the banner above says so.
     */
    enabled: !editing && (draftParam === undefined || restored || resumeFailed),
  })

  useEffect(() => {
    // ONCE. Guarded by `loaded` rather than keyed on the query settling, so a
    // background refetch can never wipe lines he has started editing.
    if (loaded || source.data === undefined) return
    setLoaded(true)
    const seed = linesFromInvoice(source.data, nextKey)
    setState((current) =>
      stateFromQuotation(
        source.data,
        seed.lines.length === 0 ? current.lines : seed.lines,
        { copy: !editing, today: current.date },
      ),
    )
    setCustomer(
      source.data.customer === null
        ? null
        : ({ id: source.data.customerId, name: source.data.customer.name } as Customer),
    )
  }, [loaded, source.data, editing])

  /*
   * HYDRATE THE CHOSEN-CUSTOMER CARD. A loaded quotation arrives with a
   * customerId and a frozen snapshot name; the card needs the whole live row
   * (GSTIN, state, the CGST-vs-IGST hint). Never overwrites a row the picker
   * chose — the condition only fires while the row in hand is partial.
   */
  const hydrate = useCustomerById(
    state.customerId !== null && (customer === null || customer.stateCode === undefined)
      ? state.customerId
      : null,
  )
  useEffect(() => {
    if (hydrate.data === undefined) return
    setCustomer(hydrate.data)
  }, [hydrate.data])

  const patch = (changes: Partial<QuotationEditorState>) => {
    setState((current) => ({ ...current, ...changes }))
  }

  const patchLine = (key: string, changes: Partial<EditorLine>) => {
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
    }))
  }

  const addLine = (parentKey: string | null = null) => {
    setState((current) => ({
      ...current,
      lines: [...current.lines, { ...emptyLine(nextKey()), parentKey }],
    }))
  }

  const addGroup = () => {
    const headerKey = nextKey()
    setState((current) => ({
      ...current,
      lines: [
        ...current.lines,
        { ...emptyLine(headerKey), isGroupHeader: true },
        { ...emptyLine(nextKey()), parentKey: headerKey },
      ],
    }))
  }

  const removeLine = (key: string) => {
    setState((current) => ({
      // A header takes its children with it — §4 allows two levels, and an
      // orphaned child would be a priced line with no job title above it.
      ...current,
      lines: current.lines.filter((line) => line.key !== key && line.parentKey !== key),
    }))
  }

  const body = toQuotationApiBody(state)
  const canSave = body !== null && preview.data !== undefined && !save.isPending

  /*
   * PREVIEW — the unsaved form as the SHEET IT WOULD BECOME, DRAFT-watermarked
   * corner to corner by the same renderer a saved quotation uses.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * IT MATTERS MORE HERE THAN ON A BILL, AND §9.3 IS THE REASON. This is the
   * document that goes to the customer, and what it PRINTS differs from what this
   * screen shows: under TAX_EXTRA — the default, and what the real Excel
   * quotations do — the sheet prints the literal word "EXTRA" against the tax
   * rows and a PRE-TAX figure under "G. Total", while the totals bar below shows
   * computed tax. The bar's `emphasiseSubtotal` is the nearest thing the editor
   * has to saying so, and it is a headline figure rather than the sheet.
   *
   * NOTHING ABOUT §9.3 IS DECIDED HERE. The server fabricates the response and
   * the RENDERER resolves the printed presentation from `taxDisplayMode` and the
   * totals, exactly as it does for a saved quotation — one rule, one place, and
   * this codebase has got §9.3 wrong five times by having two.
   *
   * A NEW TAB, because the form must survive the look: navigating away from a
   * half-typed quotation to see it would defeat the point. The object URL is
   * revoked a minute later, which is long enough for the tab to take the bytes
   * and short enough not to pin every preview's blob in memory.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const [previewing, setPreviewing] = useState(false)
  const onPreviewPdf = async () => {
    if (body === null) return
    setPreviewing(true)
    try {
      /*
       * TWO IDS, AND THEY MEAN DIFFERENT THINGS.
       *
       * `draft` is the saved FORM's id, so the number box prints DRAFT/QUOT/12 —
       * never a predicted quotation number, which is allocated inside the save
       * (§6). `editing` is the saved QUOTATION this form would replace, which is
       * what makes the sheet print the buyer the `PUT` keeps (§4) instead of the
       * live customer row. They never both appear — autosave is off while
       * revising — but the pair is built properly rather than by luck.
       */
      const params = new URLSearchParams()
      if (autosave.draftUsedId !== null) params.set("draft", String(autosave.draftUsedId))
      if (editing && id !== undefined) params.set("editing", id)
      const suffix = params.size === 0 ? "" : `?${params.toString()}`
      const fetched = await requestBlob(`${endpoints.quotationPreviewPdf}${suffix}`, {
        body,
        fallbackFilename: "draft-preview.pdf",
      })
      const url = URL.createObjectURL(fetched.blob)
      window.open(url, "_blank", "noopener")
      window.setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 60_000)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("err.somethingWentWrong"))
    } finally {
      setPreviewing(false)
    }
  }

  const onSave = () => {
    if (body === null) return
    save.mutate(body, {
      onSuccess: (result) => {
        toast.success(t("status.saved"))
        /*
         * The quotation exists with a number now, so the form has done its job.
         * Leaving it would show a second, numberless copy of a document that is
         * already in the register — and the owner would have no way to tell
         * which of the two is the offer he sent.
         */
        void autosave.completed()
        /*
         * THE WARNINGS ARE NOT THROWN AWAY. The server reports what it DECIDED
         * differently — a rate overruled by the versioned master (§8.7), an
         * archived buyer — and on a quotation those decide what the customer is
         * about to be quoted. `duration: Infinity` because a 4-second auto-hide
         * is how a warning gets technically shown and never read.
         */
        for (const warning of result.warnings ?? []) {
          toast.warning(warning, { duration: Infinity, closeButton: true })
        }
        void navigate(routes.quotation(result.data.id), { replace: true })
      },
    })
  }

  // An edit whose document is still on its way: the form must not render empty
  // and invite typing into a state that is about to be replaced.
  if (editing && source.isPending) return <EditorSkeleton />

  if (editing && source.isError) {
    const notFound = source.error instanceof ApiError && source.error.status === 404
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert variant="destructive">
          <AlertTitle>{notFound ? t("err.notFound") : t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription>
            {source.error instanceof ApiError
              ? source.error.message
              : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  /*
   * THE SERVER'S RULE, RE-READ AT RENDER. `isEditable` is DRAFT-or-SENT and the
   * `PUT` asserts it again, so this is a courtesy, not the gate — but without it
   * a link followed from a stale register would present a full editor over an
   * approved offer and reject the save at the very end, after the retyping.
   */
  if (editing && source.data !== undefined && !source.data.isEditable) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert>
          <AlertTitle>{t("quotation.lockedTitle")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{t("quotation.lockedHelp")}</span>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                nativeButton={false}
                render={<Link to={routes.quotation(source.data.id)} />}
              >
                {t("btn.viewQuotation")}
              </Button>
              <Button
                nativeButton={false}
                render={<Link to={routes.quotationCopy(source.data.id)} />}
              >
                {t("btn.makeACopy")}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-32 md:gap-6 md:p-6 md:pb-32">
      <BackLink />

      {/* The same header grammar as the quotation screen it becomes: identity
        * left, status badge beside it, so dad learns the layout once. */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight tabular-nums">
              {editing && source.data !== undefined
                ? t("quotation.editTitle", { number: source.data.number })
                : t("quotation.newTitle")}
            </h1>
            {editing && source.data !== undefined ? (
              <DocumentStatusBadge status={source.data.status} kind="quotation" />
            ) : null}
            {/* The saved form's own name, sitting exactly where the document's
              * status badge sits — the two never appear together, because one
              * means "no number yet" and the other belongs to a numbered
              * quotation. */}
            {!editing && autosave.draftUsedId !== null ? (
              <DraftNameBadge kind="QUOTATION" id={autosave.draftUsedId} status={autosave.status} />
            ) : null}
          </div>
          <p className="text-muted-foreground text-sm">
            {/* §6 — a new quotation's number is allocated inside the save
              * transaction so the series has no gaps; there is nothing honest
              * to show yet. An edit keeps the number it already has. */}
            {editing ? t("quotation.editSubtitle") : t("quotation.numberOnSave")}
          </p>
        </div>

        {/*
          * THE SAME CONTROLS, IN THE SAME PLACE, AS THE BILL EDITOR (§7.2) —
          * the autosave clock, "Save now", and the READY handoff. Dad
          * learns this row once and it means the same thing on both screens.
          *
          * Absent while REVISING a saved quotation: that document is already
          * stored under its own number and the save button at the bottom is
          * what writes to it.
          */}
        {editing ? null : (
          <div className="flex flex-wrap items-center gap-2" role="status">
            {autosave.saving ? (
              <Badge
                variant="outline"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-sm font-medium [&>svg]:size-4!"
              >
                <Spinner data-icon="inline-start" />
                {t("draft.saving")}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">
                {autosave.savedAt !== null
                  ? t("draft.savedAt", {
                      time: autosave.savedAt.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })
                  : t("draft.notYet")}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={!autosave.canSaveNow || autosave.saving}
              onClick={autosave.saveNow}
            >
              <SaveIcon />
              {t("btn.saveNow")}
            </Button>
            <DraftReadyControl
              status={autosave.status}
              exists={autosave.draftUsedId !== null}
              pending={autosave.settingStatus}
              onChange={autosave.changeStatus}
            />
          </div>
        )}
      </header>

      {/* The other device won a concurrent edit of this form (§7.2). Autosave
        * has stopped; reloading adopts the newer version. */}
      {autosave.conflict !== null ? <DraftConflictAlert kind="QUOTATION" /> : null}

      {/*
        * THE RESUME FAILED, AND SAYING SO BEATS A BLANK FORM. The draft was
        * turned into a document or discarded elsewhere; autosave has been
        * reopened (see `enabled`) so typing from here starts a fresh draft
        * rather than disappearing.
        */}
      {resumeFailed ? (
        <Alert>
          <AlertTitle>{t("draft.goneTitle")}</AlertTitle>
          <AlertDescription>{t("draft.goneHelp")}</AlertDescription>
        </Alert>
      ) : null}

      {save.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
          <AlertDescription>
            {save.error instanceof ApiError ? save.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("quotation.aboutTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>{t("lbl.customer")}</Label>
            <ChosenCustomer
              customer={customer}
              onChange={() => {
                setPickingCustomer(true)
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="quotation-subject">{t("lbl.subject")}</Label>
            <Input
              id="quotation-subject"
              value={state.subject}
              onChange={(event) => {
                patch({ subject: event.target.value })
              }}
              maxLength={191}
            />
            <p className="text-muted-foreground text-xs">{t("quotation.subjectHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-date">{t("lbl.date")}</Label>
            <DateField
              id="quotation-date"
              value={state.date}
              onChange={(value) => {
                patch({ date: value })
              }}
            />
            {/* §8.7 — the date selects the GST rate period. Not cosmetic. */}
            <p className="text-muted-foreground text-xs">{t("invoice.dateHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-valid">{t("lbl.validUntil")}</Label>
            <DateField
              id="quotation-valid"
              value={state.validUntil}
              onChange={(value) => {
                patch({ validUntil: value })
              }}
              clearable
            />
            <p className="text-muted-foreground text-xs">{t("quotation.validUntilHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-attn">{t("lbl.contactName")}</Label>
            <Input
              id="quotation-attn"
              value={state.attnName}
              onChange={(event) => {
                patch({ attnName: event.target.value })
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-po">{t("lbl.poRef")}</Label>
            <Input
              id="quotation-po"
              value={state.poRefNo}
              onChange={(event) => {
                patch({ poRefNo: event.target.value })
              }}
              maxLength={64}
            />
            <p className="text-muted-foreground text-xs">{t("quotation.poRefHelp")}</p>
          </div>

          {/*
            * BESIDE THE P.O. REFERENCE, because that is where §9.3 prints it —
            * the two are one thought ("their order, their line on it"), and the
            * owner asked where to populate them in the same sentence.
            *
            * BLANK IS A REAL ANSWER. Leaving it empty prints the footer's
            * write-in rule for a pen, exactly as every quotation did before this
            * field existed, so there is nothing to warn about and no default to
            * invent.
            */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-item-no">{t("lbl.itemNo")}</Label>
            <Input
              id="quotation-item-no"
              value={state.itemNo}
              onChange={(event) => {
                patch({ itemNo: event.target.value })
              }}
              // The column is VARCHAR(64) and the contract caps it there too;
              // stopping the typing beats a 400 after the whole sheet is filled.
              maxLength={64}
              spellCheck={false}
            />
            <p className="text-muted-foreground text-xs">{t("quotation.itemNoHelp")}</p>
          </div>

          {/*
            * ═════════════════════════════════════════════════════════════════
            * THE VENDOR CODE, SHOWN BUT NEVER TYPED HERE. The owner could not
            * find where to set it, and the answer is that it is NOT a property
            * of this document: it lives on the CUSTOMER (`customers.vendor_code`,
            * edited on the customer screen) and the server copies it onto each
            * quotation at save time (§4). It prints as VENDOR CODE in §9.3's
            * footer, next to the two fields above, which is exactly why it looks
            * like it ought to be typed alongside them.
            *
            * SO IT IS DISPLAYED, NOT EDITABLE. A per-quotation override would be
            * a second copy of the customer's own code, and the two would drift
            * the first time somebody corrected one of them. `toQuotationApiBody`
            * sends no `vendorCode` key at all, and the create schema has none to
            * receive it.
            *
            * `readOnly`, NOT `disabled` — the same decision as the derived PAN on
            * CustomerEditorScreen, and for the same complaint: disabled paints a
            * real value in placeholder-grey and it reads as "not filled in".
            * Full-strength text on a muted ground says "a fact, not a box".
            *
            * ONLY ONCE A CUSTOMER IS CHOSEN. With no buyer there is no code to
            * show, and an empty labelled box on a blank form is a question the
            * screen cannot answer.
            * ═════════════════════════════════════════════════════════════════
            */}
          {customer === null ? null : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quotation-vendor-code">{t("lbl.vendorCode")}</Label>
              <Input
                id="quotation-vendor-code"
                value={customer.vendorCode ?? ""}
                readOnly
                tabIndex={-1}
                // A dash rather than an empty box: this customer has not given
                // this business a code, which is ordinary and worth saying.
                placeholder="—"
                spellCheck={false}
                className="bg-muted text-foreground"
              />
              <p className="text-muted-foreground text-xs">{t("quotation.vendorCodeHelp")}</p>
            </div>
          )}

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="quotation-tax-mode">{t("quotation.taxModeLabel")}</Label>
            {/*
              * §9.3. This changes WHAT THE SHEET SAYS, never what the tax is:
              * the engine computes the same CGST/SGST/IGST either way and the
              * server stores both figures. Under TAX_EXTRA the sheet prints the
              * pre-tax figure under "G. Total" with "GST EXTRA" against the tax
              * rows — which is how this business's existing quotations read, and
              * why it is the default.
              */}
            <Select
              value={state.taxDisplayMode}
              /* Without this Base UI's `Select.Value` prints the RAW value —
                * `TAX_EXTRA` — on the closed trigger, because it cannot know what
                * the item rendered. One table feeds both, so the closed control
                * and the open list cannot say different things. */
              itemToStringLabel={(mode) => t(TAX_MODE_LABEL_KEY[mode])}
              onValueChange={(mode) => {
                // `onValueChange` is typed to allow null; there is no empty option
                // here and §9.3 has no "unset" mode, so nothing is the right
                // response rather than writing a null into the state.
                if (mode === null) return
                patch({ taxDisplayMode: mode })
              }}
            >
              <SelectTrigger id="quotation-tax-mode" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TAX_MODES.map((mode) => (
                  <SelectItem className={TOUCH_ITEM} key={mode} value={mode}>
                    {t(TAX_MODE_LABEL_KEY[mode])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">{t("quotation.taxModeHelp")}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("line.heading")}</CardTitle>
          <CardDescription>{t("line.serverRecomputes")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {state.lines.map((line) => (
            <LineCard
              key={line.key}
              line={line}
              lines={state.lines}
              preview={preview.data?.data.lines.find((row) => row.key === line.key)}
              // An offer is exactly where a discount is negotiated (§15(3)(a)).
              discountable
              onChange={(changes) => {
                patchLine(line.key, changes)
              }}
              onRemove={() => {
                removeLine(line.key)
              }}
              onPickItem={() => {
                setPickingItemFor(line.key)
              }}
              onAddChild={() => {
                addLine(line.key)
              }}
            />
          ))}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                addLine()
              }}
            >
              <PlusIcon />
              {t("btn.addLine")}
            </Button>
            <Button type="button" variant="outline" onClick={addGroup}>
              <ListPlusIcon />
              {t("btn.addSubLine")}
            </Button>
          </div>

          <Separator />

          {/*
            * §15(3)(a), and on a quotation it moves the headline figure. Under
            * the default TAX_EXTRA the printed `G. Total` IS the pre-tax subtotal
            * (§9.3), so a discount taken before tax is the number the customer
            * reads off the sheet. The SAME component the invoice editor uses —
            * both send the same key to the same schema.
            */}
          <DocumentDiscountField
            value={state}
            totals={preview.data?.data.totals}
            onChange={patch}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("quotation.wordsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-notes">{t("lbl.notes")}</Label>
            <Textarea
              id="quotation-notes"
              rows={3}
              value={state.notes}
              onChange={(event) => {
                patch({ notes: event.target.value })
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-terms">{t("lbl.terms")}</Label>
            <Textarea
              id="quotation-terms"
              rows={3}
              value={state.terms}
              onChange={(event) => {
                patch({ terms: event.target.value })
              }}
            />
          </div>
        </CardContent>
      </Card>

      <TotalsBar
        preview={preview}
        canSave={canSave}
        saving={save.isPending}
        incomplete={body === null}
        onSave={onSave}
        previewing={previewing}
        onPreviewPdf={() => {
          void onPreviewPdf()
        }}
        saveLabel={editing ? t("btn.saveChanges") : t("btn.makeTheQuotation")}
        // §9.3 — the headline follows the mode the sheet will print in.
        emphasiseSubtotal={state.taxDisplayMode === "TAX_EXTRA"}
      />

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(picked) => {
          setCustomer(picked)
          patch({
            customerId: picked.id,
            attnName: state.attnName === "" ? (picked.defaultContactName ?? "") : state.attnName,
          })
        }}
      />

      <ItemPicker
        open={pickingItemFor !== null}
        onOpenChange={(open) => {
          if (!open) setPickingItemFor(null)
        }}
        onPick={(item) => {
          if (pickingItemFor === null) return
          // The saved job fills the line, INCLUDING its HSN and GST rate — the
          // two fields most easily got wrong by hand (§4.1 requires an HSN on
          // every line), which is the whole reason the rate card exists.
          patchLine(pickingItemFor, {
            itemId: item.id,
            description: item.description ?? item.name,
            supplyNature: item.supplyNature as EditorLine["supplyNature"],
            hsn: item.hsn,
            gstRatePercent: basisPointsToInputValue(item.gstRateBp),
            pricingMode: item.pricingMode as EditorLine["pricingMode"],
            unitLabel: item.unitLabel,
            rate: item.ratePaise === 0 ? "" : (item.ratePaise / 100).toFixed(2),
          })
          setPickingItemFor(null)
        }}
      />
    </div>
  )
}

function BackLink(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={routes.quotations}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon className="size-4" />
      {t("nav.quotations")}
    </Link>
  )
}

function EditorSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
